import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { TrendingUp } from "lucide-react";

/**
 * AIUsageTrendChart — 14-daagse mini bar-chart per bucket (stacked) op basis van /api/ai-usage/trend.
 * Bouwt SVG zelf (geen externe chart-lib nodig).
 */
const BUCKET_LABELS = {
  ai_agent_chat: "Chat",
  ai_rapportages_genereer: "Rapport",
  ai_voice_transcribe: "Voice",
};

const BUCKET_COLORS = {
  ai_agent_chat: "#3b82f6",
  ai_rapportages_genereer: "#8b5cf6",
  ai_voice_transcribe: "#10b981",
};

export default function AIUsageTrendChart({ dagen = 14 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/ai-usage/trend?dagen=${dagen}`);
        setData(d);
      } catch (err) {
        if (err.response?.status === 403) setError("forbidden");
        else setError("error");
      }
      setLoading(false);
    })();
  }, [dagen]);

  if (loading) {
    return (
      <Card className="p-5 bg-white border-border">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded w-1/4" />
          <div className="h-32 bg-slate-100 rounded" />
        </div>
      </Card>
    );
  }
  if (error === "forbidden" || !data) return null;

  const dgn = data.dagen || [];
  const buckets = (data.buckets || []).filter((b) => dgn.some((d) => d[b] > 0));
  const activeBuckets = buckets.length > 0 ? buckets : Object.keys(BUCKET_LABELS);

  // SVG dimensies
  const W = 720;
  const H = 180;
  const PAD_L = 32;
  const PAD_R = 12;
  const PAD_T = 12;
  const PAD_B = 30;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const maxY = Math.max(1, ...dgn.map((d) => d.totaal || 0));
  const barW = Math.max(6, plotW / dgn.length - 4);
  const gap = plotW / dgn.length - barW;

  const totaalPeriode = dgn.reduce((s, d) => s + (d.totaal || 0), 0);
  const gemiddeld = (totaalPeriode / Math.max(dgn.length, 1)).toFixed(1);
  const piek = dgn.reduce((m, d) => (d.totaal > (m?.totaal || 0) ? d : m), null);

  return (
    <Card className="p-5 bg-white border-border" data-testid="ai-usage-trend">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-widest font-semibold flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3" /> AI-gebruik trend · laatste {dgn.length} dagen
          </div>
          <div className="flex items-baseline gap-4 mt-1">
            <span className="font-heading text-2xl font-bold text-slate-900">{totaalPeriode.toLocaleString("nl-NL")}</span>
            <span className="text-xs text-slate-500">totaal · gem. {gemiddeld}/dag</span>
            {piek && piek.totaal > 0 && (
              <span className="text-xs text-slate-500">
                piek: <span className="font-semibold text-slate-800">{piek.totaal}</span> op {formatDay(piek.dag)}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {activeBuckets.map((b) => (
            <span key={b} className="inline-flex items-center gap-1 text-xs text-slate-600">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: BUCKET_COLORS[b] || "#94a3b8" }} />
              {BUCKET_LABELS[b] || b}
            </span>
          ))}
        </div>
      </div>

      {totaalPeriode === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">Nog geen AI-activiteit in de laatste {dgn.length} dagen.</p>
      ) : (
        <div className="relative">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto overflow-visible" data-testid="ai-trend-svg">
            {/* Y-as lijnen (25/50/75/100%) */}
            {[0, 0.25, 0.5, 0.75, 1].map((r) => {
              const y = PAD_T + plotH - r * plotH;
              return (
                <g key={r}>
                  <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#f1f5f9" strokeWidth="1" />
                  <text x={PAD_L - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#94a3b8">
                    {Math.round(maxY * r)}
                  </text>
                </g>
              );
            })}

            {/* Stacked bars */}
            {dgn.map((d, i) => {
              const x = PAD_L + i * (barW + gap);
              let yCur = PAD_T + plotH;
              return (
                <g key={i} onMouseEnter={() => setHover(d)} onMouseLeave={() => setHover(null)}>
                  <rect
                    x={x - 1}
                    y={PAD_T}
                    width={barW + 2}
                    height={plotH}
                    fill={hover === d ? "rgba(59,130,246,0.05)" : "transparent"}
                  />
                  {activeBuckets.map((b) => {
                    const v = d[b] || 0;
                    if (v <= 0) return null;
                    const h = (v / maxY) * plotH;
                    yCur -= h;
                    return (
                      <rect
                        key={b}
                        x={x}
                        y={yCur}
                        width={barW}
                        height={h}
                        fill={BUCKET_COLORS[b] || "#94a3b8"}
                        rx="1.5"
                      />
                    );
                  })}
                  {/* Day label (om de X dagen tonen om te voorkomen dat het overloopt) */}
                  {(dgn.length <= 14 || i % 2 === 0) && (
                    <text
                      x={x + barW / 2}
                      y={H - PAD_B + 14}
                      textAnchor="middle"
                      fontSize="9"
                      fill="#64748b"
                      transform={dgn.length > 21 ? `rotate(-40 ${x + barW / 2} ${H - PAD_B + 14})` : ""}
                    >
                      {formatDay(d.dag)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Hover-tooltip */}
          {hover && (
            <div className="absolute top-0 right-0 bg-slate-900 text-white text-xs rounded-md px-3 py-2 shadow-lg pointer-events-none" data-testid="trend-tooltip">
              <div className="font-semibold mb-1">{formatDay(hover.dag, true)}</div>
              <div className="text-slate-300">{hover.totaal} requests</div>
              {activeBuckets.map((b) => {
                const v = hover[b] || 0;
                if (v <= 0) return null;
                return (
                  <div key={b} className="flex items-center gap-1.5 mt-0.5">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ background: BUCKET_COLORS[b] }} />
                    <span className="text-slate-300">{BUCKET_LABELS[b] || b}:</span>
                    <span className="font-mono">{v}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function formatDay(iso, full = false) {
  try {
    const d = new Date(iso);
    if (full) return d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" });
    return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}
